import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Subscription } from "rxjs";
import { BrandCampaign } from "../../../shared/models/brandCampaign";
import {
  BrandInsightPartner,
  BrandInsightPartnerProp,
} from "../../../shared/models/brandCampaignInsight";
import { Campaign } from "../../../shared/models/campaign";
import { CampaignPartnerStatus } from "../../../shared/enums/campaign.enums";
import {
  PartnerFacebookPageClaimStatus,
  PartnerLogoStatus,
} from "../../../shared/enums/partner.enums";
import { DialogService } from "../../../shared/services/dialog.service";
import {
  StorageKeys,
  LocalStorageService,
} from "../../../shared/services/local-storage.service";
import { NotificationService } from "../../../shared/services/notification.service";
import { BrandCampaignInsightService } from "../shared/services/brand-campaign-insight.service";
import { BrandCampaignPartnersService } from "../shared/services/brand-campaign-partners.service";
import { BrandCampaignDialogService } from "../brand-campaign-dialog.service";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";

type ColumnKey = BrandInsightPartnerProp | "actions";

interface Column {
  value: ColumnKey;
  visible: boolean;
}

@Component({
  selector: "app-brand-campaign-partners",
  templateUrl: "./brand-campaign-partners.component.html",
  styleUrl: "./brand-campaign-partners.component.scss",
})
export class BrandCampaignPartnersComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) protected sort!: MatSort;
  @ViewChild(MatPaginator, { static: true }) protected paginator!: MatPaginator;

  protected dataSource?: MatTableDataSource<BrandInsightPartner>;
  protected displayedColumns: Column[] = [];
  protected filters: Partial<Record<BrandInsightPartnerProp, string>> = {};
  protected loading = true;
  protected showColumnFilters = false;
  protected showExportOptions = false;

  private readonly subscriptions = new Subscription();
  private campaign: BrandCampaign;
  private hiddenCols: BrandInsightPartnerProp[] = ["id", "shortUrlPrefix"];
  private partners: BrandInsightPartner[] = [];
  private displayedKeys: BrandInsightPartnerProp[] = [
    "companyName",
    "companyAdditionalInfo",
    "connectedToFacebook",
    "fbAdRightsStatus",
    "logoStatus",
    "hasApprovedContentCalendar",
    "percentageCompleted",
    "status",
    "totalPublishedFacebookAds",
    "totalPublishedGoogleAds",
    "totalParticipants",
  ];

  constructor(
    private readonly brandCampaignDialogService: BrandCampaignDialogService,
    private readonly brandCampaignInsightService: BrandCampaignInsightService,
    private readonly brandCampaignPartnersService: BrandCampaignPartnersService,
    private readonly brandCampaignService: BrandCampaignService,
    private readonly dialogService: DialogService,
    private readonly localStorageService: LocalStorageService,
    private readonly notificationService: NotificationService,
  ) {
    this.campaign = this.brandCampaignService.currentBrandCampaign;
  }

  public ngOnInit(): void {
    this.getBrandCampaignPartners(this.campaign);

    const cacheKeys = this.localStorageService.get(
      StorageKeys.BrandCampaignPartnersListDisplayColumns,
    );

    if (cacheKeys) {
      const parsedKeys = JSON.parse(cacheKeys);

      if (parsedKeys) {
        this.displayedKeys = parsedKeys;
      }
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private getBrandCampaignPartners(campaign: Campaign): void {
    this.brandCampaignInsightService
      .getBrandCampaignPartnerInsights(campaign.id)
      .then((partnerInsights) => {
        this.partners = partnerInsights;
        this.displayedColumns = [];

        const keys: BrandInsightPartnerProp[] =
          this.partners && this.partners[0]
            ? (Object.keys(this.partners[0]) as BrandInsightPartnerProp[])
            : [];

        keys.forEach((key) => {
          if (!this.hiddenCols.includes(key)) {
            this.displayedColumns.push({
              visible: this.displayedKeys.includes(key),
              value: key,
            });

            this.filters[key] = "";
          }
        });

        this.displayedColumns.push({ visible: true, value: "actions" });
        this.dataSource = new MatTableDataSource(this.partners);
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.dataSource.filterPredicate = (data) => this.filterPredicate(data);
        this.loading = false;
      });
  }

  protected applyFilter(): void {
    if (this.dataSource) {
      this.dataSource.filter = JSON.stringify(this.filters);
    }
  }

  private filterPredicate(data: BrandInsightPartner): boolean {
    return Object.entries(this.filters).every(([key, value]) => {
      if (value === "") {
        return true;
      }

      const prop = key as BrandInsightPartnerProp;
      const str_data =
        typeof data[prop] === "boolean"
          ? data[prop]
            ? "yes"
            : "no"
          : String(data[prop]);

      return str_data.toLowerCase().includes(value.trim().toLowerCase());
    });
  }

  protected toggleColumn(column: Column): void {
    if (column.visible !== undefined) {
      column.visible = !column.visible;
    }

    this.localStorageService.store(
      StorageKeys.BrandCampaignPartnersListDisplayColumns,
      JSON.stringify(this.getVisibleColumnKeys()),
    );
  }

  protected getVisibleColumnKeys(): ColumnKey[] {
    if (!this.displayedColumns) {
      return [];
    }

    return this.displayedColumns
      .filter((col) => col.visible)
      .map((col) => col.value);
  }

  protected getColumnSum(column: ColumnKey): string {
    const includedColumns: BrandInsightPartnerProp[] = [
      "totalAssetsDownloaded",
      "totalScheduledAds",
      "totalPublishedAds",
      "totalPublishedFacebookAds",
      "totalPublishedGoogleAds",
      "totalGoogleImpressions",
      "totalGoogleClicks",
      "totalParticipants",
      "totalFacebookPaidAds",
      "totalGooglePaidAds",
      "totalFacebookPaidAdsBudget",
      "totalGooglePaidAdsBudget",
      "totalFacebookPaidAdsSpend",
      "totalGooglePaidAdsSpend",
      "amountCalls",
      "amountEmails",
      "totalAdBudget",
      "totalAdSpend",
      "totalAdImpressions",
      "facebookPostsScheduled",
      "facebookPostsPublished",
      "facebookPostsImpressions",
      "facebookPostsLikes",
      "facebookPostsComments",
      "facebookPostsShares",
      "facebookPostsClicks",
      "linkedinPostsScheduled",
      "linkedinPostsPublished",
      "linkedinPostsImpressions",
      "linkedinPostsComments",
      "linkedinPostsLikes",
      "linkedinPostsShares",
      "linkedinPostsClicks",
      "linkedinPostsEngagements",
      "totalInstagramPostsScheduled",
      "totalInstagramPostsPublished",
      "totalInstagramPostsImpressions",
      "totalInstagramPostsReach",
      "totalInstagramPostsEngagement",
      "totalInstagramPostsVideoViews",
      "totalInstagramPostsViews",
      "totalPostsScheduled",
      "totalPostsPublished",
      "totalPublishedFacebookAds",
      "totalScheduledFacebookAds",
      "totalFacebookAdsImpressions",
      "totalFacebookAdsReach",
      "totalFacebookAdsEngagement",
      "totalFacebookAdsClicks",
      "totalFacebookAdsSpent",
      "totalFacebookAdsBudget",
      "totalPublishedGoogleAds",
      "totalScheduledGoogleAds",
      "totalGoogleImpressions",
      "totalGoogleViews",
      "totalGoogleClicks",
      "totalGoogleAdsSpent",
      "totalGoogleBudget",
      "totalFacebookPaidAds",
      "totalGooglePaidAds",
      "totalFacebookPaidAdsBudget",
      "totalGooglePaidAdsBudget",
      "totalFacebookPaidAdsSpend",
      "totalGooglePaidAdsSpend",
      "totalPublishedAds",
      "totalScheduledAds",
      "totalAdImpressions",
      "totalAdClicks",
      "totalAdSpend",
      "totalAdBudget",
    ];

    const currencyColumns: BrandInsightPartnerProp[] = [
      "totalFacebookPaidAdsBudget",
      "totalGooglePaidAdsBudget",
      "totalFacebookPaidAdsSpend",
      "totalGooglePaidAdsSpend",
      "totalAdBudget",
      "totalAdSpend",
      "totalFacebookAdsBudget",
      "totalGoogleBudget",
      "totalFacebookAdsSpent",
      "totalGoogleAdsSpent",
    ];

    if (column === "actions") {
      return "";
    }

    if (!includedColumns.includes(column)) {
      return "";
    }

    const values = this.dataSource!.filteredData.map((item) => item[column]);
    const result = values
      .map((el) => Number(el))
      .filter((el) => !isNaN(el))
      .reduce((sum, el) => sum + Number(el), 0);

    if (currencyColumns.includes(column)) {
      return "(" + result.toFixed(2) + "" + this.campaign.currencySymbol + ")";
    }

    return "(" + result + ")";
  }

  protected getCellClass(
    value: CampaignPartnerStatus,
    column: ColumnKey,
  ): string {
    let classes = "";

    if (typeof value === "boolean") {
      classes += "text-centered ";
    }

    if (typeof value !== "string") {
      return classes;
    }

    if (column === "status") {
      classes += "colored-cell ";

      switch (value) {
        case CampaignPartnerStatus.Accepted:
          classes += "cell-yellow";
          break;
        case CampaignPartnerStatus.PendingValidation:
          classes += "cell-orange";
          break;
        case CampaignPartnerStatus.Validated:
          classes += "cell-green";
          break;
        case CampaignPartnerStatus.Engaged:
          classes += "cell-blue";
          break;
        case CampaignPartnerStatus.Cancelled:
          classes += "cell-red";
          break;
        default:
          // INVITED
          classes += "cell-grey";
          break;
      }
    }

    return classes;
  }

  protected formatData(value: any, column: ColumnKey): string {
    if (value === false) {
      return "shared.no";
    }
    if (value === true) {
      return "shared.yes";
    }
    if (!value) {
      return "-";
    }
    if (column === "status") {
      return `shared.partnerStatus.${value}`;
    }
    if (column === "contactEmails") {
      value = JSON.parse(value);
    }
    if (column === "fbAdRightsStatus") {
      value = this.getFacebookAdStatus(value);
    }
    if (column === "logoStatus") {
      value = this.getPartnerLogoStatus(value);
    }
    if (Array.isArray(value)) {
      return value.join(",");
    }
    if (column === "facebookUrl") {
      return this.wrapUrls(value);
    }
    if (
      column === "totalFacebookPaidAdsBudget" ||
      column === "totalGooglePaidAdsBudget" ||
      column === "totalFacebookPaidAdsSpend" ||
      column === "totalGooglePaidAdsSpend"
    ) {
      return value.toFixed(2) + "" + this.campaign.currencySymbol;
    }

    return value;
  }

  private wrapUrls(text: string, new_window = true): string {
    const url_pattern =
      /(?:(?:https?|ftp):\/\/)?(?:\S+(?::\S*)?@)?(?:(?!10(?:\.\d{1,3}){3})(?!127(?:\.\d{1,3}){3})(?!169\.254(?:\.\d{1,3}){2})(?!192\.168(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\u{ffff}0-9]+-?)*[a-z\u00a1-\u{ffff}0-9]+)(?:\.(?:[a-z\u00a1-\u{ffff}0-9]+-?)*[a-z\u00a1-\u{ffff}0-9]+)*(?:\.(?:[a-z\u00a1-\u{ffff}]{2,})))(?::\d{2,5})?(?:\/[^\s]*)?/giu;

    const target = new_window === true || new_window === null ? "_blank" : "";

    return text.replace(url_pattern, (url) => {
      const protocol_pattern = /^(?:(?:https?|ftp):\/\/)/i;
      const href = protocol_pattern.test(url) ? url : "http://" + url;
      return '<a href="' + href + '" target="' + target + '">' + url + "</a>";
    });
  }

  protected async openBrandNotesDialog(
    selectedPartner: BrandInsightPartner,
  ): Promise<void> {
    if (this.campaign) {
      const editedBrandInsightPartner =
        await this.brandCampaignDialogService.showPartnerEdit({
          campaign: this.campaign,
          brandInsightPartner: selectedPartner,
        });

      if (editedBrandInsightPartner) {
        const idx = this.partners.findIndex(
          (p) => editedBrandInsightPartner.id === p.id,
        );
        this.partners[idx] = editedBrandInsightPartner;
        this.dataSource = new MatTableDataSource(this.partners);
      }
    }
  }

  protected exportPartners(format: string): void {
    if (this.campaign && this.dataSource) {
      const partnerIds = this.dataSource.filteredData.map(
        (partner) => partner.id,
      );
      this.subscriptions.add(
        this.brandCampaignPartnersService
          .exportBrandCampaignPartners(
            this.campaign.id,
            this.getVisibleColumnKeys(),
            partnerIds,
            format,
          )
          .subscribe((response) => {
            if (window) {
              window.open(response.fileUrl, "_blank");
            }
          }),
      );
    }
  }

  protected showPartnerStatusChart(): void {
    if (this.campaign) {
      this.brandCampaignDialogService.showPartnerStatusChart({
        campaignId: this.campaign.id,
      });
    }
  }

  protected partnerCanBeAccepted(partner: BrandInsightPartner): boolean {
    return (
      partner.status === CampaignPartnerStatus.Cancelled ||
      partner.status === CampaignPartnerStatus.Accepted ||
      partner.status === CampaignPartnerStatus.PendingValidation
    );
  }

  protected onAcceptPartner(partner: BrandInsightPartner): void {
    this.dialogService.confirm({
      title: "brand.campaign.partnerList.acceptPartner",
      message: "brand.campaign.partnerList.acceptPartnerConfirm",
      confirmButton: "shared.yes",
      cancelButton: "shared.no",
      onConfirm: () => this.acceptPartner(partner),
    });
  }

  private acceptPartner(partner: BrandInsightPartner): void {
    this.subscriptions.add(
      this.brandCampaignPartnersService
        .updateBrandCampaignPartnerStatus(
          this.campaign.id,
          partner.id,
          CampaignPartnerStatus.Validated,
        )
        .subscribe({
          next: () => {
            partner.status = CampaignPartnerStatus.Validated;
            this.notificationService.success(
              "brand.campaign.partnerList.partnerValidated",
            );
          },
          error: () => {
            this.notificationService.error(
              "brand.campaign.partnerList.errorValidatingPartner",
            );
          },
        }),
    );
  }

  protected partnerCanBeCancelled(partner: BrandInsightPartner): boolean {
    return (
      partner.status === CampaignPartnerStatus.Validated ||
      partner.status === CampaignPartnerStatus.Engaged ||
      partner.status === CampaignPartnerStatus.Accepted ||
      partner.status === CampaignPartnerStatus.Invited ||
      partner.status === CampaignPartnerStatus.PendingValidation
    );
  }

  protected onCancelPartner(partner: BrandInsightPartner): void {
    this.dialogService.confirm({
      title: "brand.campaign.partnerList.cancelPartner",
      message: "brand.campaign.partnerList.cancelPartnerConfirm",
      confirmButton: "shared.yes",
      cancelButton: "shared.no",
      onConfirm: () => this.cancelPartner(partner),
    });
  }

  private cancelPartner(partner: BrandInsightPartner): void {
    this.subscriptions.add(
      this.brandCampaignPartnersService
        .updateBrandCampaignPartnerStatus(
          this.campaign.id,
          partner.id,
          CampaignPartnerStatus.Cancelled,
        )
        .subscribe({
          next: () => {
            partner.status = CampaignPartnerStatus.Cancelled;
            this.notificationService.success(
              "brand.campaign.partnerList.partnerCancelled",
            );
          },
          error: () => {
            this.notificationService.error(
              "brand.campaign.partnerList.errorCancellingPartner",
            );
          },
        }),
    );
  }

  private getFacebookAdStatus(status: PartnerFacebookPageClaimStatus): string {
    switch (status) {
      case PartnerFacebookPageClaimStatus.Authorized:
        return "shared.facebookAdRights.authorized";
      case PartnerFacebookPageClaimStatus.Claimed:
        return "shared.facebookAdRights.claimed";
      case PartnerFacebookPageClaimStatus.NotClaimed:
        return "shared.facebookAdRights.notClaimed";
    }
  }

  private getPartnerLogoStatus(status: PartnerLogoStatus): string {
    switch (status) {
      case PartnerLogoStatus.PartnerUploaded:
        return "shared.partnerLogoStatus.partnerUploaded";
      case PartnerLogoStatus.AdminUploaded:
        return "shared.partnerLogoStatus.adminUploaded";
      case PartnerLogoStatus.NoLogo:
        return "shared.partnerLogoStatus.noLogo";
    }
  }
}
